/**
 * Free-draw and stroke-erase gestures for `canvasDrawing` nodes: press, drag,
 * release. The pen stays down after each stroke, so one tool pick buys a whole
 * sketch. Pairs with `useCanvasDrawStyle` for tip, colour and weight.
 *
 * Gesture ownership mirrors `useShapePlacement` — capture-phase listeners on
 * the window so React Flow never starts its own marquee or pan drag while the
 * pen is down. Nothing snaps to the lattice: ink is freehand.
 *
 * Storage is written once per gesture. Samples accumulate in a ref until the
 * pointer comes up, then land as a single `addNode`; the eraser deletes as it
 * sweeps but brackets the drag in `pauseUndo`/`resumeUndo`, so either gesture
 * is one undo step and one Liveblocks round trip per stroke.
 */

import { Node, useReactFlow, useStoreApi } from "@xyflow/react";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useCanvasApi } from "../context/CanvasContext";
import { normalizeCanvasStrokeWidth } from "../types/canvasNodeData";
import { drawingsUnderSweep, ERASER_RADIUS_PX } from "../utils/drawingErase";
import {
  appendDrawingSample,
  DRAWING_MIN_SAMPLE_PX,
  DRAWING_NODE_PAD,
  DRAWING_SIMPLIFY_PX,
  drawingNodeGeometry,
  simplifyDrawingPoints,
} from "../utils/drawingStroke";
import type { DrawingTip } from "../utils/drawingTips";

/** Flow-space extent below which a dragged stroke is treated as a stray click. */
const MIN_STROKE_EXTENT = 4;

interface UseDrawPlacementOptions {
  /** The draw tool is picked; the pane is listening for a stroke. */
  active: boolean;
  tip: DrawingTip;
  color: string;
  strokeWidth: number;
  /** The eraser takes the same gesture: press, drag over ink, release. */
  erasing: boolean;
}

/** Ink lands on nodes too, not just bare canvas. Chrome is still off limits. */
function isOnPane(event: PointerEvent): boolean {
  const target = event.target as HTMLElement;
  if (target.closest("[data-canvas-control]")) return false;
  return !!target.closest(".react-flow__pane, .react-flow__node");
}

export function useDrawPlacement({
  active,
  tip,
  color,
  strokeWidth,
  erasing,
}: UseDrawPlacementOptions) {
  const { screenToFlowPosition, getNodes, getInternalNode, getZoom } =
    useReactFlow();
  const storeApi = useStoreApi();
  const { addNode, removeNodes, pauseUndo, resumeUndo } = useCanvasApi();

  /** Absolute flow-space points of the stroke in progress (ghost preview). */
  const [strokePoints, setStrokePoints] = useState<number[] | null>(null);

  // Everything the listeners read lives behind a ref: the effect re-registers
  // only when the gesture itself changes, never per sample.
  const flowRef = useRef({
    screenToFlowPosition,
    getNodes,
    getInternalNode,
    getZoom,
  });
  flowRef.current = {
    screenToFlowPosition,
    getNodes,
    getInternalNode,
    getZoom,
  };
  const styleRef = useRef({ tip, color, strokeWidth });
  styleRef.current = { tip, color, strokeWidth };
  const canvasRef = useRef({ addNode, removeNodes, pauseUndo, resumeUndo });
  canvasRef.current = { addNode, removeNodes, pauseUndo, resumeUndo };

  // Picking the pen drops the selection: its floating toolbar would sit over
  // the sketch, and pane pointerdown belongs to the gesture now, so nothing
  // else would ever clear it. Selection is local-only in @liveblocks/react-flow.
  useEffect(() => {
    if (!active) return;
    storeApi.getState().unselectNodesAndEdges();
  }, [active, storeApi]);

  useEffect(() => {
    if (!active || erasing) return;

    /** The stroke in progress; null between strokes. */
    let points: number[] | null = null;

    const toFlow = (event: PointerEvent) =>
      flowRef.current.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0 || !isOnPane(event)) return;
      event.preventDefault();
      event.stopPropagation();
      const start = toFlow(event);
      points = [start.x, start.y];
      setStrokePoints(points);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!points || event.buttons !== 1) return;
      const minDistance = DRAWING_MIN_SAMPLE_PX / flowRef.current.getZoom();
      // Coalesced moves are the samples the browser skipped delivering; a fast
      // stroke is drawn from all of them, not just the one that woke us.
      for (const sample of event.getCoalescedEvents?.() ?? [event]) {
        const position = toFlow(sample);
        appendDrawingSample(points, position.x, position.y, minDistance);
      }
      setStrokePoints([...points]);
    };

    /** Lands whatever has been drawn. The stroke ends here either way. */
    const endStroke = () => {
      const drawn = points;
      points = null;
      setStrokePoints(null);
      if (!drawn) return;

      const simplified = simplifyDrawingPoints(
        drawn,
        DRAWING_SIMPLIFY_PX / flowRef.current.getZoom(),
      );
      const { position, width, height, localPoints } =
        drawingNodeGeometry(simplified);
      // A tap leaves a dot; a drag that never travelled is a fingertip wobble.
      const extent = width + height - DRAWING_NODE_PAD * 4;
      if (drawn.length > 2 && extent < MIN_STROKE_EXTENT) return;

      const node: Node = {
        id: uuidv4(),
        type: "canvasDrawing",
        position,
        width,
        height,
        data: {
          points: localPoints,
          tip: styleRef.current.tip,
          color: styleRef.current.color,
          stroke_width: normalizeCanvasStrokeWidth(
            styleRef.current.strokeWidth,
          ),
        },
      };
      canvasRef.current.addNode(node);
    };

    const onPointerUp = (event: PointerEvent) => {
      if (event.button !== 0) return;
      // Keep the release inside this gesture: it must not reach React Flow's
      // pane handler, nor dismiss the pen flyout as an outside click.
      if (isOnPane(event)) event.stopPropagation();
      endStroke();
    };

    // A pointer the browser takes away (touch interruption, a system gesture)
    // never sends pointerup. The ink drawn so far still lands.
    const onPointerCancel = () => endStroke();

    window.addEventListener("pointerdown", onPointerDown, { capture: true });
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp, { capture: true });
    window.addEventListener("pointercancel", onPointerCancel, {
      capture: true,
    });

    return () => {
      setStrokePoints(null);
      window.removeEventListener("pointerdown", onPointerDown, {
        capture: true,
      });
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp, { capture: true });
      window.removeEventListener("pointercancel", onPointerCancel, {
        capture: true,
      });
    };
  }, [active, erasing]);

  useEffect(() => {
    if (!active || !erasing) return;

    /** Previous sample, so each move erases along a segment, not a dot. */
    let last: { x: number; y: number } | null = null;
    // Storage lags the sweep by a render, so the same stroke keeps testing as
    // a hit until it lands. Erase each one once.
    const erased = new Set<string>();

    const toFlow = (event: PointerEvent) =>
      flowRef.current.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

    const sweep = (from: { x: number; y: number }, to: typeof from) => {
      const { getNodes: nodes, getInternalNode: internalNode } =
        flowRef.current;
      const hits = drawingsUnderSweep(
        nodes(),
        from,
        to,
        internalNode,
        ERASER_RADIUS_PX / flowRef.current.getZoom(),
      ).filter((id) => !erased.has(id));
      if (hits.length === 0) return;
      hits.forEach((id) => erased.add(id));
      canvasRef.current.removeNodes(hits);
    };

    const endSweep = () => {
      if (!last) return;
      last = null;
      erased.clear();
      canvasRef.current.resumeUndo();
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0 || !isOnPane(event)) return;
      event.preventDefault();
      event.stopPropagation();
      last = toFlow(event);
      // Everything the sweep deletes collapses into one undo step.
      canvasRef.current.pauseUndo();
      sweep(last, last);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!last || event.buttons !== 1) return;
      for (const sample of event.getCoalescedEvents?.() ?? [event]) {
        const position = toFlow(sample);
        sweep(last, position);
        last = position;
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      if (event.button !== 0) return;
      if (isOnPane(event)) event.stopPropagation();
      endSweep();
    };

    // A cancelled pointer sends no pointerup, and a sweep that never ends
    // leaves the history paused — every later edit would merge into it.
    const onPointerCancel = () => endSweep();

    window.addEventListener("pointerdown", onPointerDown, { capture: true });
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp, { capture: true });
    window.addEventListener("pointercancel", onPointerCancel, {
      capture: true,
    });

    return () => {
      // A sweep interrupted by a tool switch or unmount still has to close its
      // undo group, or every later edit merges into it.
      endSweep();
      window.removeEventListener("pointerdown", onPointerDown, {
        capture: true,
      });
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp, { capture: true });
      window.removeEventListener("pointercancel", onPointerCancel, {
        capture: true,
      });
    };
  }, [active, erasing]);

  return { strokePoints };
}
